import { useState } from 'react'
import { Plus, X, Bookmark } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { SaveToLibraryDialog } from './SaveToLibraryDialog'
import { MarkdownField } from '@/features/projectBoards/components/MarkdownField'
import { fieldTypeLabel } from '../../hooks/useAdminFormBuilder'
import type { ApplicationField, ApplicationPaymentTier } from '@/types'

function formatCents(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100)
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`
  }
}

function CurrencyInput({
  valueCents,
  currency,
  onChange,
  placeholder,
}: {
  valueCents: number
  currency: string
  onChange: (cents: number) => void
  placeholder?: string
}) {
  return (
    <Input
      inputMode="numeric"
      value={formatCents(valueCents, currency)}
      placeholder={placeholder}
      onChange={(e) => {
        const digits = e.target.value.replace(/\D/g, '')
        onChange(digits === '' ? 0 : Number(digits))
      }}
      onKeyDown={(e) => {
        if (e.key === 'Backspace') {
          e.preventDefault()
          onChange(Math.floor(valueCents / 10))
        }
      }}
    />
  )
}

function cryptoRandomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

interface Props {
  field: ApplicationField | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpdate: (patch: Partial<ApplicationField>) => void
}

export function FieldConfigPanel({ field, open, onOpenChange, onUpdate }: Props) {
  const [saveLibraryOpen, setSaveLibraryOpen] = useState(false)

  if (!field) return null
  const f = field  // narrowed non-null reference

  const showOptions = ['dropdown', 'multiple_choice', 'checkbox_group'].includes(f.type)
  const showScale = f.type === 'linear_scale'
  const showFile = f.type === 'file_upload'
  const showPlaceholder = ['short_text', 'long_text', 'email', 'phone', 'url', 'number', 'text_list', 'mailing_list'].includes(f.type)
  const isMailingList = f.type === 'mailing_list'
  const showTextList = f.type === 'text_list'
  const showPayment = f.type === 'payment'
  const isNotice = f.type === 'notice'
  const paymentMode: 'fixed' | 'tiered' = (f.paymentTiers?.length ?? 0) > 0 ? 'tiered' : 'fixed'

  function setPaymentMode(mode: 'fixed' | 'tiered') {
    if (mode === 'fixed') {
      onUpdate({ paymentTiers: undefined })
    } else {
      onUpdate({
        paymentAmountCents: undefined,
        paymentTiers: f.paymentTiers ?? [
          { id: cryptoRandomId(), label: 'Standard', amountCents: 2500 },
        ],
      })
    }
  }

  function updateTier(idx: number, patch: Partial<ApplicationPaymentTier>) {
    const tiers = [...(f.paymentTiers ?? [])]
    const existing = tiers[idx]
    tiers[idx] = { ...existing, ...patch } as ApplicationPaymentTier
    onUpdate({ paymentTiers: tiers })
  }

  function addTier() {
    onUpdate({
      paymentTiers: [
        ...(f.paymentTiers ?? []),
        { id: cryptoRandomId(), label: `Tier ${(f.paymentTiers?.length ?? 0) + 1}`, amountCents: 2500 },
      ],
    })
  }

  function removeTier(idx: number) {
    const tiers = (f.paymentTiers ?? []).filter((_, i) => i !== idx)
    onUpdate({ paymentTiers: tiers.length ? tiers : undefined })
  }

  function updateOption(index: number, value: string) {
    const options = [...(f.options ?? [])]
    options[index] = value
    onUpdate({ options })
  }

  function addOption() {
    onUpdate({ options: [...(f.options ?? []), `Option ${(f.options?.length ?? 0) + 1}`] })
  }

  function removeOption(index: number) {
    const options = (f.options ?? []).filter((_: string, i: number) => i !== index)
    onUpdate({ options })
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full overflow-y-auto px-6 sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{fieldTypeLabel(field.type)} Settings</SheetTitle>
          </SheetHeader>

          <div className="mt-6 space-y-5">
            <div className="space-y-1">
              <Label>{isNotice ? 'Title (optional)' : 'Question Label'}</Label>
              <Input
                value={f.label}
                onChange={(e) => onUpdate({ label: e.target.value })}
                placeholder={isNotice ? 'e.g. Please read before continuing' : 'Enter question…'}
              />
            </div>

            {/* Description / Notice content (markdown) */}
            <div className="space-y-1">
              <Label>{isNotice ? 'Notice content' : 'Helper Text (optional)'}</Label>
              <MarkdownField
                value={f.description ?? ''}
                onChange={(v) => onUpdate({ description: v })}
                placeholder={
                  isNotice
                    ? 'Write the disclaimer or notice shown to applicants. Markdown and [links](https://example.com) supported.'
                    : 'Additional context shown below the label. Markdown supported.'
                }
                height={isNotice ? 260 : 180}
                // The Sheet already locks document scrolling. Letting the editor
                // manage body overflow would preserve Radix's temporary hidden
                // value after the Sheet closes.
                overflow={false}
              />
            </div>

            {/* Placeholder */}
            {showPlaceholder && (
              <div className="space-y-1">
                <Label>{isMailingList ? 'Checkbox consent text' : 'Placeholder'}</Label>
                <Input
                  value={f.placeholder ?? ''}
                  onChange={(e) => onUpdate({ placeholder: e.target.value })}
                  placeholder={isMailingList ? 'Yes, please add me to the ODD mailing list.' : 'e.g. Type your answer here…'}
                />
                {isMailingList && (
                  <p className="text-xs text-muted-foreground">
                    Text shown next to the checkbox. The field label above acts as the question; the description is the preamble.
                  </p>
                )}
              </div>
            )}

            {/* Required */}
            {!isNotice && (
            <div className="flex items-center gap-3">
              <input
                id="required"
                type="checkbox"
                className="h-4 w-4 accent-primary"
                checked={f.required}
                onChange={(e) => onUpdate({ required: e.target.checked })}
              />
              <Label htmlFor="required">Required</Label>
            </div>
            )}

            {/* Options (dropdown / choice / checkbox) */}
            {showOptions && (
              <div className="space-y-2">
                <Label>Options</Label>
                <div className="space-y-2">
                  {(f.options ?? []).map((opt: string, i: number) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        value={opt}
                        onChange={(e) => updateOption(i, e.target.value)}
                        placeholder={`Option ${i + 1}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="shrink-0"
                        onClick={() => removeOption(i)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addOption}>
                  <Plus className="mr-1 h-3 w-3" />
                  Add Option
                </Button>
              </div>
            )}

            {f.type === 'number' && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="number-min">Minimum</Label>
                    <Input id="number-min" type="number" value={f.min ?? ''}
                      onChange={(e) => onUpdate({ min: e.target.value === '' ? undefined : Number(e.target.value) })} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="number-max">Maximum</Label>
                    <Input id="number-max" type="number" value={f.max ?? ''}
                      onChange={(e) => onUpdate({ max: e.target.value === '' ? undefined : Number(e.target.value) })} />
                  </div>
                </div>
                <Label className="flex items-center gap-2">
                  <input type="checkbox" checked={f.integerOnly ?? false}
                    onChange={(e) => onUpdate({ integerOnly: e.target.checked })} />
                  Whole numbers only
                </Label>
              </div>
            )}

            {/* Linear Scale */}
            {showScale && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Min Value</Label>
                    <Input
                      type="number"
                      value={f.min ?? 1}
                      onChange={(e) => onUpdate({ min: Number(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Max Value</Label>
                    <Input
                      type="number"
                      value={f.max ?? 5}
                      onChange={(e) => onUpdate({ max: Number(e.target.value) })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Min Label</Label>
                    <Input
                      value={f.minLabel ?? ''}
                      onChange={(e) => onUpdate({ minLabel: e.target.value })}
                      placeholder="e.g. Not at all"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Max Label</Label>
                    <Input
                      value={f.maxLabel ?? ''}
                      onChange={(e) => onUpdate({ maxLabel: e.target.value })}
                      placeholder="e.g. Extremely"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* File Upload */}
            {showFile && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label>Max File Size (MB)</Label>
                  <Input
                    type="number"
                    value={f.maxFileSizeMB ?? 10}
                    onChange={(e) => onUpdate({ maxFileSizeMB: Number(e.target.value) })}
                    min={1}
                    max={100}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Accepted File Types</Label>
                  <Input
                    value={(f.acceptedFileTypes ?? []).join(', ')}
                    onChange={(e) =>
                      onUpdate({
                        acceptedFileTypes: e.target.value
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean),
                      })
                    }
                    placeholder="image/*, .pdf, .docx"
                  />
                  <p className="text-xs text-muted-foreground">
                    Comma-separated. Leave blank to accept all files.
                  </p>
                </div>
              </div>
            )}

            {/* Text List */}
            {showTextList && (
              <div className="space-y-1">
                <Label>Max Entries (optional)</Label>
                <Input
                  type="number"
                  value={f.maxEntries ?? ''}
                  onChange={(e) =>
                    onUpdate({ maxEntries: e.target.value === '' ? undefined : Number(e.target.value) })
                  }
                  min={1}
                  placeholder="Unlimited"
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to allow unlimited entries.
                </p>
              </div>
            )}

            {/* Payment */}
            {showPayment && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label>Description (optional)</Label>
                  <Textarea
                    rows={2}
                    value={f.paymentDescription ?? ''}
                    onChange={(e) => onUpdate({ paymentDescription: e.target.value })}
                    placeholder="Shown to the applicant above the payment selector"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Currency</Label>
                    <Input
                      value={(f.paymentCurrency ?? 'usd').toUpperCase()}
                      onChange={(e) => onUpdate({ paymentCurrency: e.target.value.toLowerCase().trim() || 'usd' })}
                      maxLength={3}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Mode</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={paymentMode === 'fixed' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPaymentMode('fixed')}
                      >
                        Fixed
                      </Button>
                      <Button
                        type="button"
                        variant={paymentMode === 'tiered' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPaymentMode('tiered')}
                      >
                        Tiered
                      </Button>
                    </div>
                  </div>
                </div>

                {paymentMode === 'fixed' ? (
                  <div className="space-y-1">
                    <Label>Amount</Label>
                    <CurrencyInput
                      valueCents={f.paymentAmountCents ?? 2500}
                      currency={f.paymentCurrency ?? 'usd'}
                      onChange={(cents) => onUpdate({ paymentAmountCents: cents })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Type digits like a cash register — "5000" becomes $50.00. Stripe minimum is $0.50.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Tiers</Label>
                    {(f.paymentTiers ?? []).map((tier, i) => (
                      <div key={tier.id} className="space-y-2 rounded-md border p-3">
                        <div className="flex items-center gap-2">
                          <Input
                            value={tier.label}
                            onChange={(e) => updateTier(i, { label: e.target.value })}
                            placeholder="Tier name"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="shrink-0"
                            onClick={() => removeTier(i)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <CurrencyInput
                          valueCents={tier.amountCents}
                          currency={f.paymentCurrency ?? 'usd'}
                          onChange={(cents) => updateTier(i, { amountCents: cents })}
                          placeholder="Amount"
                        />
                        <Input
                          value={tier.description ?? ''}
                          onChange={(e) => updateTier(i, { description: e.target.value })}
                          placeholder="Tier description (optional)"
                        />
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={addTier}>
                      <Plus className="mr-1 h-3 w-3" />
                      Add Tier
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Save to library */}
            <div className="pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSaveLibraryOpen(true)}
              >
                <Bookmark className="mr-2 h-4 w-4" />
                Save to Field Library
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <SaveToLibraryDialog
        open={saveLibraryOpen}
        onOpenChange={setSaveLibraryOpen}
        field={f}
        onSaved={() => {}}
      />
    </>
  )
}
